import { useState, useEffect } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface BankAccount {
  bankName: string;
  iban: string;
  bic: string;
}

interface Employee {
  id: number;
  employeeId: string;        // matricule
  firstName: string;
  lastName: string;
  position: string;
  department: string;
  contractType: 'CDI' | 'CDD' | 'Stage' | 'Freelance';
  status: 'Actif' | 'Congé' | 'Démissionné' | 'Suspendu';
  baseSalary: number;
  hireDate: string;
  birthDate?: string;
  birthPlace?: string;
  socialSecurityNumber?: string;
  address?: string;
  phone?: string;
  email?: string;
  bankAccount: BankAccount;
}

interface EmployeesListProps {
  onEdit: (employee: Employee) => void;
  onAdd: () => void;
  onCreatePayroll: (employee: Employee) => void;
}

export default function EmployeesList({ onEdit, onAdd, onCreatePayroll }: EmployeesListProps) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  // Chargement des employés depuis l'API
  const loadEmployees = async () => {
    setLoading(true);
    try {
      const response = await getResources(`employes`); // À adapter si ton endpoint est différent
      setEmployees(response.data);
    } catch (error: any) {
      toast.error('Erreur lors du chargement des employés');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  // Filtrage
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch =
      employee.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (employee.department?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);

    const matchesStatus = statusFilter === 'all' || employee.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Couleurs
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Actif': return 'bg-green-100 text-green-800';
      case 'Congé': return 'bg-yellow-100 text-yellow-800';
      case 'Démissionné': return 'bg-red-100 text-red-800';
      case 'Suspendu': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getContractTypeColor = (type: string) => {
    switch (type) {
      case 'CDI': return 'bg-blue-100 text-blue-800';
      case 'CDD': return 'bg-purple-100 text-purple-800';
      case 'Stage': return 'bg-orange-100 text-orange-800';
      case 'Freelance': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Statistiques
  const activeEmployees = employees.filter(emp => emp.status === 'Actif').length;
  const onLeaveEmployees = employees.filter(emp => emp.status === 'Congé').length;
  const totalSalaryBudget = employees
    .filter(emp => emp.status === 'Actif')
    .reduce((sum, emp) => sum + emp.baseSalary, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Chargement des employés...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Bouton Ajouter (en haut à droite) */}
      <div className="flex justify-end">
        <button
          onClick={onAdd}
          className="bg-indigo-600 text-white px-5 py-3 rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
        >
          <i className="ri-add-line text-lg"></i>
          <span>Ajouter un employé</span>
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 mr-4">
              <i className="ri-team-line text-xl text-blue-600"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Employés</p>
              <p className="text-2xl font-bold text-gray-900">{employees.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 mr-4">
              <i className="ri-user-check-line text-xl text-green-600"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Employés Actifs</p>
              <p className="text-2xl font-bold text-gray-900">{activeEmployees}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 mr-4">
              <i className="ri-calendar-line text-xl text-yellow-600"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">En Congé</p>
              <p className="text-2xl font-bold text-gray-900">{onLeaveEmployees}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 mr-4">
              <i className="ri-money-euro-circle-line text-xl text-purple-600"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Masse Salariale</p>
              <p className="text-2xl font-bold text-gray-900">{totalSalaryBudget.toLocaleString()} €</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Rechercher un employé..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full md:w-80"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="all">Tous les statuts</option>
              <option value="Actif">Actif</option>
              <option value="Congé">En Congé</option>
              <option value="Démissionné">Démissionné</option>
              <option value="Suspendu">Suspendu</option>
            </select>
          </div>

          <div className="text-sm text-gray-600">
            {filteredEmployees.length} employé(s) trouvé(s)
          </div>
        </div>
      </div>

      {/* Liste des employés */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employé</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Poste</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contrat</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salaire de Base</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date d'Embauche</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEmployees.map((employee) => (
                <tr key={employee.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-blue-600">
                            {employee.firstName.charAt(0)}{employee.lastName.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {employee.firstName} {employee.lastName}
                        </div>
                        <div className="text-sm text-gray-500">ID: {employee.employeeId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{employee.position}</div>
                    <div className="text-sm text-gray-500">{employee.department || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getContractTypeColor(employee.contractType)}`}>
                      {employee.contractType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(employee.status)}`}>
                      {employee.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {employee.baseSalary.toLocaleString()} €
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(employee.hireDate).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => setSelectedEmployee(employee)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded cursor-pointer"
                        title="Voir les détails"
                      >
                        <i className="ri-eye-line text-lg"></i>
                      </button>
                      <button
                        onClick={() => onEdit(employee)}
                        className="text-indigo-600 hover:text-indigo-900 p-1 rounded cursor-pointer"
                        title="Modifier"
                      >
                        <i className="ri-edit-line text-lg"></i>
                      </button>
                      {employee.status === 'Actif' && (
                        <button
                          onClick={() => onCreatePayroll(employee)}
                          className="text-green-600 hover:text-green-900 p-1 rounded cursor-pointer"
                          title="Créer une fiche de paie"
                        >
                          <i className="ri-money-dollar-circle-line text-lg"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredEmployees.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Aucun employé trouvé
            </div>
          )}
        </div>
      </div>

      {/* Modal de détails (inchangé, mais avec données réelles) */}
      {selectedEmployee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900">Détails de l'employé</h3>
              <button
                onClick={() => setSelectedEmployee(null)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-6">
              {/* Informations personnelles */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Informations Personnelles</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nom complet</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedEmployee.firstName} {selectedEmployee.lastName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Matricule</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedEmployee.employeeId}</p>
                  </div>
                  {selectedEmployee.birthDate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Date de naissance</label>
                      <p className="mt-1 text-sm text-gray-900">{new Date(selectedEmployee.birthDate).toLocaleDateString('fr-FR')}</p>
                    </div>
                  )}
                  {selectedEmployee.socialSecurityNumber && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">N° Sécurité Sociale</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedEmployee.socialSecurityNumber}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Coordonnées */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Coordonnées</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedEmployee.address && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Adresse</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedEmployee.address}</p>
                    </div>
                  )}
                  {selectedEmployee.phone && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Téléphone</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedEmployee.phone}</p>
                    </div>
                  )}
                  {selectedEmployee.email && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedEmployee.email}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Contractuelles */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Informations Contractuelles</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700">Poste</label><p className="mt-1 text-sm text-gray-900">{selectedEmployee.position}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700">Département</label><p className="mt-1 text-sm text-gray-900">{selectedEmployee.department || '-'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700">Date d'embauche</label><p className="mt-1 text-sm text-gray-900">{new Date(selectedEmployee.hireDate).toLocaleDateString('fr-FR')}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700">Type de contrat</label><p className="mt-1 text-sm text-gray-900">{selectedEmployee.contractType}</p></div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Statut</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 ${getStatusColor(selectedEmployee.status)}`}>
                      {selectedEmployee.status}
                    </span>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700">Salaire de base</label><p className="mt-1 text-sm text-gray-900">{selectedEmployee.baseSalary.toLocaleString()} €</p></div>
                </div>
              </div>

            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setSelectedEmployee(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md cursor-pointer"
              >
                Fermer
              </button>
              <button
                onClick={() => {
                  setSelectedEmployee(null);
                  onEdit(selectedEmployee);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md cursor-pointer"
              >
                Modifier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}